import type { NpsQuestion, NpsSurvey } from '@/shared/types/nps';
import {
  createQuestion,
  createSurvey,
  reorderQuestions,
  type QuestionPayload,
  type SurveyPayload,
} from '../api/use-nps';

/** A api-v2 valida com zod `optional()`: `null` vindo do banco é recusado, então sai do payload. */
function withoutNulls<T extends object>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== null && v !== undefined)) as T;
}

/** Dados da pesquisa copiados como estão (traduções inclusas); a cópia nasce em rascunho. */
export function surveyCopyPayload(
  source: NpsSurvey,
  overrides: { title: string; ownerId?: string },
): SurveyPayload {
  const identified = source.response_type === 'IDENTIFIED';
  return withoutNulls<SurveyPayload>({
    title: overrides.title.trim(),
    description: source.description ?? undefined,
    i18n: source.i18n ?? undefined,
    response_type: source.response_type,
    response_expiration_time: identified ? source.response_expiration_time : undefined,
    identification_required: identified ? Boolean(source.identification_required) : false,
    answer_bonus: identified ? Boolean(source.answer_bonus) : false,
    user_id: overrides.ownerId,
  });
}

export function questionCopyPayload(question: NpsQuestion): QuestionPayload {
  return withoutNulls<QuestionPayload>({
    title: question.title,
    description: question.description ?? undefined,
    i18n: question.i18n ?? undefined,
    required: Boolean(question.required),
    response_type: question.response_type,
    response_metadata: question.response_metadata ?? undefined,
  });
}

export function sortedQuestions(survey: NpsSurvey): NpsQuestion[] {
  return [...(survey.questions ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

export class DuplicateSurveyError extends Error {
  constructor(
    message: string,
    /** Pesquisa já criada quando a cópia parou (sem endpoint de exclusão, fica para o usuário conferir). */
    readonly surveyId: string | null,
    readonly copiedQuestions: number,
  ) {
    super(message);
  }
}

/**
 * Cria a pesquisa e as perguntas na ordem original. Sem endpoint de cópia na
 * api-v2: é uma chamada por pergunta, então o progresso vai para a UI.
 */
export async function duplicateSurvey(
  source: NpsSurvey,
  overrides: { title: string; ownerId?: string },
  onProgress?: (copied: number, total: number) => void,
): Promise<NpsSurvey> {
  const questions = sortedQuestions(source);
  const survey = await createSurvey(surveyCopyPayload(source, overrides));

  const created: NpsQuestion[] = [];
  for (const question of questions) {
    onProgress?.(created.length, questions.length);
    try {
      created.push(await createQuestion(survey.id, questionCopyPayload(question)));
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'erro inesperado';
      throw new DuplicateSurveyError(
        `Parou na pergunta "${question.title}": ${reason}`,
        survey.id,
        created.length,
      );
    }
  }
  onProgress?.(created.length, questions.length);

  // A api-v2 cria pergunta sem posição; grava a ordem da original.
  if (created.length > 1) {
    await reorderQuestions(
      survey.id,
      created.map((question, position) => ({ id: question.id, position })),
    ).catch(() => undefined);
  }
  return survey;
}
